import React, { useEffect, useRef, useState } from "react";
import OnevocaSearchInput from "../common/OnevocaSearchInput";
import styles from "./GroupSelectScreen.module.css";
import { useTranslation } from "react-i18next";
import {
  deleteGroups as deleteGroupsFromAPI,
  fetchGroups as fetchGroupsFromAPI,
  updateGroupCollection,
  updateGroupsCollection,
} from "./groupAPI";
import Group, { getAllGroup } from "./Group";
import GroupSelectCell from "./GroupSelectCell";
import { Status } from "../common/Status";
import LoadingScreen from "../common/LoadingScreen";
import { cloneDeep } from "lodash";
import BigButton from "../common/BigButton";
import { Snackbar } from "@mui/material";
import GroupAddScreen from "./GroupAddScreen";
import OnevocaDialog from "../common/OnevocaDialog";
import BasicMenu, { BasicMenuButton } from "../common/BasicMenu";
import { deleteGroup as deleteGroupFromAPI } from "./groupAPI";
import { sortedByNumbers } from "./groups";
import ListEmptyText from "../common/ListEmptyText";
import CollectionListScreen from "../collection/screen/CollectionListScreen";
import ContainerNavigationView from "../common/view/ContainerNavigationView";
import { ReactComponent as CloseIcon } from "../../img/iconImgAllClose.svg";
import ListSettingView from "../common/ListSettingView";
import SpacingView from "../common/view/SpaingView";
import { handleErrorAlert } from "../common/OnevocaServerError";
import { fetchCollectionList } from "../collection/service/collectionAPI";
import Collection, {
  getDefaultCollection,
} from "../collection/model/Collection";

interface GroupSelectScreenProps {
  open: boolean;
  showAllGroup: boolean;
  showNoGroup: boolean;
  selectStyle: "single" | "multiple";
  showGroupAddButton: boolean;
  handleOnSelectGroups: (groups: Group[]) => void;
  handleOnClose: () => void;
}

export default function GroupSelectScreen(props: GroupSelectScreenProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [scrollGroupname, setScrollGroupname] = useState<string>();

  const [isLoading, setIsLoading] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<Collection>();
  const [originGroups, setOriginGroups] = useState<Group[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupsToShow, setGroupsToShow] = useState<Group[]>([]);
  const [groupDeleteStatus, setGroupDeleteStatus] = useState(Status.Ready);
  const [selectedCount, setSelectedCount] = useState(0);
  const [openGroupAddScreen, setOpenGroupAddScreen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const [groupEditSelectedItem, setGroupEditSelectedItem] =
    useState<Group | null>(null);
  const [showGroupMoveItem, setShowGroupMoveItem] = useState<Group | null>(
    null
  );
  const [showGroupEditItem, setShowGroupEditItem] = useState<Group | null>(
    null
  );
  const [showGroupDeleteItem, setShowGroupDeleteItem] = useState<Group | null>(
    null
  );
  const [
    showingSelectedGroupDeleteDialog,
    setShowingSelectedGroupDeleteDialog,
  ] = useState(false);
  const [selectedGroupsForMove, setSelectedGroupsForMove] = useState<Group[]>();
  const [showingGroupMerge, setShowingGroupMerge] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<Group[] | null>(null);

  const [showingEditButton, setShowingEditButton] = useState(false);
  const [showingSelectedEditMenu, setShowingSelectedEditMenu] = useState(false);
  const [showingCollectionList, setShowingCollectionList] = useState(false);

  const selectedEditMenu: BasicMenuButton[] = [
    {
      title: t("SelectedGroupMoveButton", { selectedCount }),
      handleOnClose: () => handleOnCloseSelectedGroupMoveButton(),
    },
    {
      title: t("SelectedGroupMergeButton", { selectedCount }),
      handleOnClose: () => handleOnClickSelectedGroupMergeButton(),
    },
    {
      title: t("SelectedGroupDeleteButton", { selectedCount }),
      isWarning: true,
      handleOnClose: () => handleOnClickSelectedGroupDeleteButton(),
    },
  ];

  const selectedEditMenuWithoutMerge: BasicMenuButton[] = [
    {
      title: t("SelectedGroupMoveButton", { selectedCount }),
      handleOnClose: () => handleOnCloseSelectedGroupMoveButton(),
    },
    {
      title: t("SelectedGroupDeleteButton", { selectedCount }),
      isWarning: true,
      handleOnClose: () => handleOnClickSelectedGroupDeleteButton(),
    },
  ];

  const moreButtons: BasicMenuButton[] = [
    {
      title: t("MoveItemMenu"),
      handleOnClose: () => handleOnCloseMoveButton(),
    },
    {
      title: t("Edit"),
      handleOnClose: () => handleOnCloseEditButton(),
    },
    {
      title: t("Delete"),
      isWarning: true,
      handleOnClose: () => handleOnCloseDeleteButton(),
    },
  ];

  useEffect(() => {
    if (props.open === true) {
      loadCollectionsAndGroups(true);
    }
  }, [props.open]);

  useEffect(() => {
    switch (groupDeleteStatus) {
      case Status.Ready:
        setIsLoading(false);
        break;
      case Status.Loading:
        setIsLoading(true);
        break;
      case Status.Done:
        setIsLoading(false);
        break;
    }
  }, [groupDeleteStatus]);

  useEffect(() => {
    setGroupsToShow(cloneDeep(groups));
  }, [groups]);

  useEffect(() => {
    const selectedGroups = groupsToShow.filter((group) => group.selected);
    setSelectedGroups(selectedGroups);
    setSelectedCount(selectedGroups.length);
    updateShowingEditButton(selectedGroups.length);
    if (scrollGroupname !== undefined) {
      scrollToGroup(scrollGroupname);
      setScrollGroupname(undefined);
    }
  }, [groupsToShow]);

  function handleOnClickBackground() {
    close();
  }

  function scrollToGroup(groupname: string) {
    const container = scrollRef.current;
    const targetEl = groupRefs.current.get(groupname);

    if (container && targetEl) {
      const offsetTop = targetEl.offsetTop;
      container.scrollTo({ top: offsetTop, behavior: "smooth" });
    }
  }

  function handleOnClickGroupSelectContainer(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    e.stopPropagation();
  }

  function updateShowingEditButton(selectedCount: number) {
    if (
      selectedCount < 1 ||
      !props.showGroupAddButton ||
      isEmptyGroupSelected() ||
      isAllGroupSelected()
    ) {
      setShowingEditButton(false);
    } else {
      setShowingEditButton(true);
    }
  }

  function isAllGroupSelected(): boolean {
    return groupsToShow
      .filter((item) => item.selected)
      .some((item) => item.isAllGroup);
  }

  function isEmptyGroupSelected(): boolean {
    return groupsToShow
      .filter((item) => item.selected)
      .some((item) => item.isUnspecifiedGroup);
  }

  function getSelectedGroups(): Group[] {
    const retuningGroups = [...groupsToShow];
    return retuningGroups.filter((item) => item.selected);
  }

  function getSelectedGroupsTitle(): string[] {
    return groupsToShow
      .filter((item) => item.selected)
      .map((item) => item.title);
  }

  function initSelectedCollection(
    collections: Collection[],
    groups: Group[]
  ): Collection {
    const uniqueCollectionIds = new Set(groups.map((g) => g.collectionId));
    if (uniqueCollectionIds.size === 1) {
      const collectionId = Array.from(uniqueCollectionIds)[0];
      return (
        collections.find((c) => c.id === collectionId) ??
        getDefaultCollection(0)
      );
    } else {
      return getDefaultCollection(0);
    }
  }

  function validateSelectedCollection(
    collections: Collection[],
    selectedCollection: Collection
  ): Collection {
    const isValid = collections.some((c) => c.id === selectedCollection?.id);
    return isValid ? selectedCollection : getDefaultCollection(0);
  }

  function filteredGroups(groups: Group[], collection: Collection): Group[] {
    if (collection.isDefaultCollection) {
      return groups;
    }
    return groups.filter((item) => item.collectionId === collection.id);
  }

  function updateAllGroupIfNeeded(groups: Group[]): Group[] {
    if (!props.showAllGroup) {
      return groups;
    }

    const filteredGroups = groups.filter((group) => !group.isAllGroup);
    filteredGroups.unshift(getAllGroup(filteredGroups));

    return filteredGroups;
  }

  async function loadCollectionsAndGroups(
    initializingSelectedCollection: boolean
  ) {
    try {
      const [collections, groups] = await Promise.all([
        loadCollections(),
        loadGroups(),
      ]);

      let newSelectedCollection = selectedCollection;

      if (initializingSelectedCollection) {
        newSelectedCollection = initSelectedCollection(collections, groups);
      }

      newSelectedCollection = validateSelectedCollection(
        collections,
        newSelectedCollection!
      );

      let newGroups = filteredGroups(groups, newSelectedCollection!);
      newGroups = updateAllGroupIfNeeded(newGroups);

      setSelectedCollection(newSelectedCollection);
      setCollections(collections);
      setGroups(newGroups);
      setOriginGroups(groups);
    } catch (error) {
      handleErrorAlert(error);
    }
  }

  async function loadCollections(): Promise<Collection[]> {
    const collections = await fetchCollectionList(true);
    return collections;
  }

  async function loadGroups() {
    const groups = await fetchGroupsFromAPI();
    return groups;
  }

  function updateSelectedCollection(collection: Collection) {
    let newGroups = [...originGroups];
    newGroups = filteredGroups(newGroups, collection);
    newGroups = updateAllGroupIfNeeded(newGroups);
    setSelectedCollection(collection);
    setGroups(newGroups);
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }

  function searchGroups(searchText: string) {
    const filteredGroups = groups.filter((group) =>
      group.title.toLowerCase().includes(searchText.toLowerCase())
    );

    const newGroups = sortedByNumbers(filteredGroups);
    setGroupsToShow(newGroups);
  }

  function toggleGroupSelected(group: Group) {
    const groupIndex = groupsToShow.findIndex((g) => g.title === group.title);
    let newGroups: Group[] = [];
    switch (props.selectStyle) {
      case "single":
        newGroups = groupsToShow.map((g) => ({
          ...g,
          selected: false,
        }));
        break;
      case "multiple":
        if (group.isAllGroup) {
          newGroups = groupsToShow.map((g) => ({
            ...g,
            selected: false,
          }));
        } else {
          newGroups = groupsToShow.map((g) => {
            if (g.isAllGroup) {
              return { ...g, selected: false };
            }
            return g;
          });
        }
        break;
    }
    if (groupIndex !== -1) {
      newGroups[groupIndex].selected = !newGroups[groupIndex].selected;
    }
    setGroupsToShow(newGroups);
  }

  function handleOnClickDoneButton() {
    let selectedGroups: Group[] = [];

    const isDefaultCollectionSelected =
      selectedCollection?.isDefaultCollection ?? true;

    if (isAllGroupSelected() && !isDefaultCollectionSelected) {
      selectedGroups = groupsToShow.filter((group) => !group.isAllGroup);
    } else {
      selectedGroups = groupsToShow.filter((group) => group.selected);
    }

    props.handleOnSelectGroups(selectedGroups);
    close();
  }

  function close() {
    setGroups([]);
    props.handleOnClose();
  }

  function handleOnClickGroupAddButton() {
    setOpenGroupAddScreen(true);
  }

  function handleOnCloseMoreMenu() {
    setGroupEditSelectedItem(null);
  }

  function handleOnCloseSelectedMenu() {
    setShowingSelectedEditMenu(false);
  }

  function handleOnCloseMoveButton() {
    setShowGroupMoveItem(cloneDeep(groupEditSelectedItem));
    setGroupEditSelectedItem(null);
  }

  function handleOnCloseEditButton() {
    setShowGroupEditItem(cloneDeep(groupEditSelectedItem));
    setGroupEditSelectedItem(null);
  }

  function handleOnCloseDeleteButton() {
    setShowGroupDeleteItem(cloneDeep(groupEditSelectedItem));
    setGroupEditSelectedItem(null);
  }

  function handleOnCloseSelectedGroupMoveButton() {
    const selectedGroups = getSelectedGroups();
    setSelectedGroupsForMove(selectedGroups);
    setShowingSelectedEditMenu(false);
  }

  function handleOnClickSelectedGroupMergeButton() {
    setShowingSelectedEditMenu(false);
    setShowingGroupMerge(true);
  }

  function handleOnClickSelectedGroupDeleteButton() {
    setShowingSelectedEditMenu(false);
    setShowingSelectedGroupDeleteDialog(true);
  }

  async function changeGroupCollection(groupId: number, collectionId: number) {
    setIsLoading(true);
    try {
      await updateGroupCollection(groupId, collectionId);
      loadCollectionsAndGroups(false);
    } catch (error) {
      handleErrorAlert(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function changeGroupsCollection(
    groupsIds: number[],
    collectionId: number
  ) {
    setIsLoading(true);
    try {
      await updateGroupsCollection(groupsIds, collectionId);
      loadCollectionsAndGroups(false);
    } catch (error) {
      handleErrorAlert(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function deleteSelectedGroupServerData() {
    setGroupDeleteStatus(Status.Loading);
    try {
      const selectedGroups = getSelectedGroupsTitle();
      await deleteGroupsFromAPI(selectedGroups);
      deleteGroups(selectedGroups);
      setGroupDeleteStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setGroupDeleteStatus(Status.Ready);
    }
  }

  async function deleteGroupServerData(group: Group) {
    setGroupDeleteStatus(Status.Loading);
    try {
      await deleteGroupFromAPI(group.title);
      deleteGroup(group);
      setGroupDeleteStatus(Status.Done);
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
      setGroupDeleteStatus(Status.Ready);
    }
  }

  function deleteGroup(group: Group) {
    const groupIndex = groups.findIndex((g) => g.title === group.title);
    const newGroups = [...groups];
    if (groupIndex !== -1) {
      newGroups.splice(groupIndex, 1);
    }
    setGroups(newGroups);
  }

  function deleteGroups(selectedGroups: string[]) {
    const newGroups = groups.filter(
      (item) => !selectedGroups.includes(item.title)
    );
    setGroups(newGroups);
  }

  if (props.open === true) {
    return (
      <>
        <div className={styles.stack}>
          <div
            className={styles.container}
            onClick={() => handleOnClickBackground()}
          >
            <div
              className={styles.groupSelectContainer}
              onClick={handleOnClickGroupSelectContainer}
            >
              <div className={styles.topContainer}>
                <ContainerNavigationView
                  open={true}
                  title={t("SelectGroupButton")}
                  leftImageButtonImage={CloseIcon}
                  rightTextButtonText={t("GroupAddButton")}
                  rightTextButtonColor="var(--text-accent-color)"
                  onClickLeftImageButton={props.handleOnClose}
                  onClickRightTextButton={handleOnClickGroupAddButton}
                />
                <div className={styles.searchContainer}>
                  <OnevocaSearchInput
                    hint={t("FindGroupNamePlaceHolderMessage")}
                    handleSearch={(searchText: string) => {
                      if (
                        searchText !== null &&
                        searchText !== undefined &&
                        searchText.length !== 0
                      ) {
                        searchGroups(searchText);
                      } else {
                        setGroupsToShow(cloneDeep(groups));
                      }
                    }}
                  />
                </div>
                <SpacingView height={24} />
                {selectedCollection !== undefined && (
                  <div className={styles.collectionContainer}>
                    <ListSettingView
                      title=""
                      value={selectedCollection?.title ?? ""}
                      handleOnClick={() => {
                        setShowingCollectionList(true);
                      }}
                    />
                    <SpacingView height={1} />
                  </div>
                )}
              </div>
              <div ref={scrollRef} className={styles.listContainer}>
                {groupsToShow.length > 0 &&
                  groupsToShow.map((group, index) => (
                    <div
                      ref={(el) => el && groupRefs.current.set(group.title, el)}
                      key={index}
                    >
                      <GroupSelectCell
                        group={group}
                        collections={collections}
                        handleOnClickCell={(group: Group) =>
                          toggleGroupSelected(group)
                        }
                        handleOnClickMoreButton={(
                          e: React.MouseEvent<HTMLDivElement>,
                          group: Group
                        ) => {
                          setAnchorEl(e.currentTarget);
                          setGroupEditSelectedItem(group);
                        }}
                      />
                    </div>
                  ))}
                {groups.length === 0 && isLoading === false && (
                  <div className={styles.emptyListTextContainer}>
                    <ListEmptyText text={t("GroupListEmptyLabel")} />
                  </div>
                )}
              </div>
              <div className={styles.buttonContainer}>
                {showingEditButton && (
                  <BigButton
                    title={t("Edit")}
                    style="clear"
                    handleClick={(e) => {
                      if (selectedCount !== 0 && e !== undefined) {
                        setAnchorEl(e?.currentTarget);
                        setShowingSelectedEditMenu(true);
                      }
                    }}
                  />
                )}
                <BigButton
                  title={t("SelectDoneButton")}
                  style={selectedCount === 0 ? "disable" : "normal"}
                  handleClick={() => {
                    if (selectedCount !== 0) {
                      handleOnClickDoneButton();
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <GroupAddScreen
            open={openGroupAddScreen}
            collectionId={selectedCollection?.id ?? 0}
            group={null}
            selectedGroups={null}
            onCreateGroup={(groupname: string) => {
              setScrollGroupname(groupname);
              setToastMessage(t("GroupCreatedToastMessage"));
              loadCollectionsAndGroups(false);
            }}
            onUpdateGroup={() => {}}
            onClose={() => {
              setOpenGroupAddScreen(false);
            }}
          />
          <GroupAddScreen
            open={showingGroupMerge && selectedCollection !== undefined}
            collectionId={0}
            group={null}
            selectedGroups={selectedGroups}
            onCreateGroup={() => {}}
            onUpdateGroup={() => {
              setToastMessage(t("TermEditedToastMessage"));
              loadCollectionsAndGroups(false);
            }}
            onClose={() => {
              setShowingGroupMerge(false);
            }}
          />
          <GroupAddScreen
            open={showGroupEditItem !== null}
            collectionId={showGroupEditItem?.collectionId ?? 0}
            group={showGroupEditItem}
            selectedGroups={null}
            onCreateGroup={() => {}}
            onUpdateGroup={() => {
              setToastMessage(t("TermEditedToastMessage"));
              loadCollectionsAndGroups(false);
            }}
            onClose={() => {
              setShowGroupEditItem(null);
            }}
          />
          <CollectionListScreen
            open={showingCollectionList}
            showDefaultCollection={true}
            onSelectCollection={updateSelectedCollection}
            onUpdateCollection={() => {
              loadCollectionsAndGroups(false);
            }}
            onClose={() => {
              setShowingCollectionList(false);
            }}
          />
          <CollectionListScreen
            open={showGroupMoveItem !== null}
            showDefaultCollection={false}
            onSelectCollection={(collection) => {
              if (showGroupMoveItem !== null) {
                changeGroupCollection(showGroupMoveItem.id, collection.id);
              }
              setShowGroupMoveItem(null);
            }}
            onUpdateCollection={() => {
              loadCollectionsAndGroups(false);
            }}
            onClose={() => {
              setShowGroupMoveItem(null);
            }}
          />
          <CollectionListScreen
            open={selectedGroupsForMove !== undefined}
            showDefaultCollection={false}
            onSelectCollection={(collection) => {
              if (selectedGroupsForMove !== undefined) {
                changeGroupsCollection(
                  selectedGroupsForMove?.map((group) => group.id),
                  collection.id
                );
              }
              setSelectedGroupsForMove(undefined);
            }}
            onUpdateCollection={() => {
              loadCollectionsAndGroups(false);
            }}
            onClose={() => {
              setSelectedGroupsForMove(undefined);
            }}
          />
          <BasicMenu
            isOpen={groupEditSelectedItem !== null}
            anchorEl={anchorEl}
            buttons={moreButtons}
            handleOnClose={() => handleOnCloseMoreMenu()}
          />
          <BasicMenu
            isOpen={showingSelectedEditMenu}
            anchorEl={anchorEl}
            buttons={
              selectedCount > 1
                ? selectedEditMenu
                : selectedEditMenuWithoutMerge
            }
            handleOnClose={() => handleOnCloseSelectedMenu()}
          />
          <OnevocaDialog
            open={showingSelectedGroupDeleteDialog}
            title={null}
            message={t("groupdeletemessage")}
            buttons={[
              {
                title: t("Delete"),
                style: "solid",
                onClick: () => {
                  setShowingSelectedGroupDeleteDialog(false);
                  deleteSelectedGroupServerData();
                },
              },
              {
                title: t("Cancel"),
                style: "normal",
                onClick: () => {
                  setShowingSelectedGroupDeleteDialog(false);
                },
              },
            ]}
          />
          <OnevocaDialog
            open={showGroupDeleteItem !== null}
            title={showGroupDeleteItem ? showGroupDeleteItem.title : null}
            message={t("groupdeletemessage")}
            buttons={[
              {
                title: t("Delete"),
                style: "solid",
                onClick: () => {
                  if (showGroupDeleteItem !== null) {
                    setShowGroupDeleteItem(null);
                    deleteGroupServerData(showGroupDeleteItem);
                  }
                },
              },
              {
                title: t("Cancel"),
                style: "normal",
                onClick: () => {
                  setShowGroupDeleteItem(null);
                },
              },
            ]}
          />
          <Snackbar
            open={toastMessage !== null}
            autoHideDuration={3000}
            onClose={() => {
              setToastMessage(null);
            }}
            message={toastMessage}
          />
          {isLoading === true && <LoadingScreen />}
        </div>
      </>
    );
  } else {
    return null;
  }
}
